import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const sections = [
  { id: 'relationship', title: '1. Relationship of the Parties' },
  { id: 'acceptance-meta', title: '2. Acceptance of Meta Terms' },
  { id: 'messaging-compliance', title: '3. Messaging Compliance & Opt-in' },
  { id: 'prohibited-content', title: '4. Prohibited Content' },
  { id: 'tech-provider', title: '5. Role of 6Miles as Tech Provider' },
  { id: 'data-privacy', title: '6. Data Privacy & Security' },
  { id: 'termination', title: '7. Termination' },
];

export function TermsPage() {
  const [activeSection, setActiveSection] = useState('relationship');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="terms-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">Terms of Use</h1>
          <p className="text-lg sm:text-xl text-gray-600">for "Giri" or "GiriChat"</p>
          <p className="text-sm text-gray-500 mt-2">Last Updated: January 27, 2026</p>
        </div>
      </div>

      <div className="flex-1 py-8 sm:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Introduction */}
          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-4 sm:p-6">
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
              Welcome to Giri also known as GiriChat (the "App"), a technology platform owned and operated by <strong>6Miles</strong> ("the Company", "we", "us", or "our"). By accessing or using the GiriChat to connect with the WhatsApp Business API, you ("the Customer") agree to be bound by these Terms of Use, our Privacy Policy, and all applicable policies provided by Meta Platforms, Inc. ("Meta").
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Sticky Navigation */}
            <div className="lg:col-span-1">
              <nav className="lg:sticky lg:top-24 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                  Table of Contents
                </h2>
                <ul className="space-y-1 sm:space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`text-left w-full py-2 px-3 rounded transition-colors text-xs sm:text-sm ${
                          activeSection === section.id
                            ? 'bg-[#E8F9EE] text-[#00D95F] font-semibold'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <div className="prose prose-sm sm:prose-lg max-w-none">
                <section id="relationship" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">1. Relationship of the Parties</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Giri is a software application provided by 6Miles that serves as a <strong>Technical Provider interface</strong> for WhatsApp Business Services. All legal obligations, billing, and corporate communications regarding the App are handled by 6Miles.
                  </p>
                </section>

                <section id="acceptance-meta" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">2. Acceptance of Meta Terms</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    By using Giri, you explicitly agree to comply with the following Meta and WhatsApp terms:
                  </p>
                  <div className="space-y-3">
                    <a 
                      href="https://www.whatsapp.com/legal/business-solution-terms/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#00D95F] hover:bg-[#E8F9EE] transition-colors group"
                    >
                      <div className="w-10 h-10 bg-[#00D95F] rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="white"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-gray-900 group-hover:text-[#00D95F]">WhatsApp Business Solution Terms</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#00D95F]" />
                    </a>

                    <a 
                      href="https://www.whatsapp.com/legal/business-policy/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#00D95F] hover:bg-[#E8F9EE] transition-colors group"
                    >
                      <div className="w-10 h-10 bg-[#00D95F] rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-gray-900 group-hover:text-[#00D95F]">WhatsApp Business Messaging Policy</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#00D95F]" />
                    </a>

                    <a 
                      href="https://developers.facebook.com/policy/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#00D95F] hover:bg-[#E8F9EE] transition-colors group"
                    >
                      <div className="w-10 h-10 bg-[#1877F2] rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-gray-900 group-hover:text-[#00D95F]">Meta Developer Policies</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#00D95F]" />
                    </a>
                  </div>
                </section>

                <section id="messaging-compliance" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">3. Messaging Compliance & Opt-in</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    You represent and warrant that:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="text-gray-700">You have obtained <strong>explicit, verifiable consent (Opt-in)</strong> from all recipients before sending a business-initiated message via Giri.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="text-gray-700">You will <strong>honor all Opt-out requests immediately</strong>.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="text-gray-700">You will <strong>not use Giri to send spam or unsolicited messages</strong> that may damage the reputation of the Giri App or 6Miles.</span>
                    </li>
                  </ul>
                </section>

                <section id="prohibited-content" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">4. Prohibited Content</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    You shall not use the Giri App to transmit content that is unlawful, fraudulent, or violates Meta's restricted industry policies (e.g., weapons, tobacco, adult content).
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 text-sm">
                      <strong>⚠️ Warning:</strong> 6Miles reserves the right to suspend any account found in violation of these standards.
                    </p>
                  </div>
                </section>

                <section id="tech-provider" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">5. Role of 6Miles as a Tech Provider</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Giri acts as an intermediary. You acknowledge that:
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                      <span className="text-gray-700">6Miles is <strong>not responsible</strong> for any suspension of your account by Meta for policy violations.</span>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                      <span className="text-gray-700">You are <strong>responsible for all Meta-imposed messaging fees</strong>.</span>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                      <span className="text-gray-700">While we strive for 100% uptime, Giri's functionality is <strong>dependent on Meta's infrastructure and API availability</strong>.</span>
                    </div>
                  </div>
                </section>

                <section id="data-privacy" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">6. Data Privacy & Security</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    We process data according to the <a href="/privacy" className="text-[#00D95F] hover:underline font-semibold">Giri Privacy Policy</a> hosted on 6miles.io.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    You are responsible for maintaining the security of your Giri login credentials and for all activities that occur under your WhatsApp Business Account (WABA).
                  </p>
                </section>

                <section id="termination" className="mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">7. Termination</h2>
                  <p className="text-gray-700 leading-relaxed">
                    6Miles reserves the right to terminate your access to the Giri App if you violate these terms, Meta's policies, or if requested by Meta Platforms, Inc.
                  </p>
                </section>

                {/* Contact Section */}
                <section className="mb-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Contact Information</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    If you have any questions about these Terms of Use, please contact us:
                  </p>
                  <div className="bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] border border-[#00D95F]/20 rounded-lg p-6">
                    <p className="text-gray-700 mb-2"><strong>Company:</strong> 6Miles</p>
                    <p className="text-gray-700 mb-2"><strong>Email:</strong> info@6miles.io</p>
                    <p className="text-gray-700"><strong>Address:</strong> A32 Juhu Sun N Sea bldg, Juhu Tara Rd, Santacruz West, Mumbai 400049, India</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
